import { useEffect, useState } from "react";

// ── Dictator Host Screen ──────────────────────────────────────────────────────

const ACCENT = "#FFD700";

function Timer({ seconds, max }) {
  const pct = Math.max(0, seconds / max);
  const isLow = seconds <= 10;
  const color = isLow ? "#ff3355" : "#3dffa0";
  const r = 20, circ = 2 * Math.PI * r;
  return (
    <svg width={52} height={52} viewBox="0 0 52 52">
      <circle cx={26} cy={26} r={r} fill="none" stroke="#222" strokeWidth={4} />
      <circle cx={26} cy={26} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
        strokeLinecap="round" transform="rotate(-90 26 26)"
        style={{ transition: "stroke-dashoffset 1s linear", filter: `drop-shadow(0 0 4px ${color})` }}
      />
      <text x={26} y={31} textAnchor="middle" fill={isLow ? "#ff3355" : "#fff"}
        fontSize={14} fontFamily="'Rubik Mono One', monospace">{seconds}</text>
    </svg>
  );
}

export default function DictatorHost({ data, law, voteData, result, players, onEndDiscussion, tv }) {
  const fs = (a, b) => tv ? a : b;
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerMax, setTimerMax] = useState(60);
  const [prevPhase, setPrevPhase] = useState(null);

  useEffect(() => {
    const p = data?.phase;
    if (!p || p === prevPhase) return;
    setPrevPhase(p);
    if (p === "discussion") { setTimerMax(60); setTimeLeft(60); }
    else if (p === "vote")  { setTimerMax(30); setTimeLeft(30); }
    else setTimeLeft(null);
  }, [data?.phase]); // eslint-disable-line

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  // ── Result ────────────────────────────────────────────────────────────────
  if (data?.phase === "result" && result) {
    const sorted = Object.entries(result.scores || {}).sort((a, b) => b[1] - a[1]);
    const votesFor = Object.values(result.votes || {}).filter(v => v === result.most_accused).length;
    const totalVoters = Object.keys(result.votes || {}).length;
    const consensus = result.most_accused && totalVoters > 0 && votesFor > totalVoters / 2;
    return (
      <div className="w-full animate-slide-up" style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: fs("1.2rem", "0.75rem"), maxWidth: "700px", margin: "0 auto",
      }}>
        <div style={{ fontSize: fs("2.5rem", "1.8rem") }}>⚖️</div>
        <div className="font-heading text-center" style={{ fontSize: fs("1.4rem", "1rem"), color: ACCENT }}>
          Закон: «{result.law}»
        </div>
        {result.most_accused ? (
          <div style={{
            background: consensus ? "#ff335522" : "#ffffff0a",
            border: `2px solid ${consensus ? "#ff3355" : "#555"}`,
            borderRadius: "1rem", padding: fs("0.8rem 1.5rem", "0.5rem 1rem"), textAlign: "center",
          }}>
            <div className="font-heading" style={{ color: consensus ? "#ff3355" : "#aaa", fontSize: fs("1rem", "0.8rem") }}>
              {consensus ? "👮 Нарушитель пойман!" : "🤔 Нет единого мнения"}
            </div>
            <div className="font-display text-white" style={{ fontSize: fs("1.5rem", "1.1rem") }}>
              {result.most_accused}
            </div>
            <div className="font-heading text-[#555]" style={{ fontSize: fs("0.72rem", "0.6rem") }}>
              {votesFor} из {totalVoters} голосов
            </div>
          </div>
        ) : (
          <div className="font-heading text-[#555]" style={{ fontSize: fs("1rem", "0.8rem") }}>Никто не проголосовал</div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", justifyContent: "center" }}>
          {Object.entries(result.votes || {}).map(([voter, accused]) => (
            <div key={voter} style={{
              background: "#ffffff0a", border: "1.5px solid #ffffff22",
              borderRadius: "2rem", padding: "0.2rem 0.75rem",
              fontSize: fs("0.85rem", "0.7rem"), color: "#ccc",
            }}>
              {voter} → <span style={{ color: accused === result.most_accused ? "#ff3355" : ACCENT }}>{accused}</span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", width: "100%", maxWidth: "340px" }}>
          {sorted.map(([name, score], i) => {
            const pl = players.find(p => p.name === name);
            return (
              <div key={name} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                background: i === 0 ? "#ffe60015" : "#ffffff08",
                border: `1.5px solid ${i === 0 ? "#ffe60055" : "#ffffff11"}`,
                borderRadius: "0.6rem", padding: fs("0.4rem 0.75rem", "0.3rem 0.6rem"),
              }}>
                <span className="font-heading" style={{ color: i === 0 ? "#ffe600" : "#fff", fontSize: fs("1rem", "0.8rem") }}>
                  {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : "  "} {name}
                  {name === result.dictator && <span style={{ color: ACCENT, fontSize: "0.7em" }}> 👑</span>}
                </span>
                <span className="font-display" style={{ color: pl?.color || "#aaa", fontSize: fs("1rem", "0.8rem") }}>{score}</span>
              </div>
            );
          })}
        </div>
        <div className="font-heading text-[#555]" style={{ fontSize: fs("0.8rem", "0.65rem") }}>
          Раунд {result.round}/{result.max_rounds} · Следующий раунд...
        </div>
      </div>
    );
  }

  // ── Voting ────────────────────────────────────────────────────────────────
  if (data?.phase === "vote" && voteData) {
    const voters = (voteData.players || []).filter(p => p !== voteData.dictator);
    return (
      <div className="w-full animate-slide-up" style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: fs("1rem", "0.65rem"), maxWidth: "700px", margin: "0 auto",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ fontSize: fs("1.8rem", "1.3rem") }}>🗳️</span>
          <span className="font-heading text-white" style={{ fontSize: fs("1.4rem", "1rem") }}>Голосование</span>
          {timeLeft !== null && <Timer seconds={timeLeft} max={timerMax} />}
        </div>
        <div style={{
          background: "#FFD70022", border: `2px solid ${ACCENT}`,
          borderRadius: "1rem", padding: fs("0.8rem 1.5rem", "0.5rem 1rem"),
          textAlign: "center", width: "100%",
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize: fs("0.8rem", "0.65rem") }}>Закон</div>
          <div className="font-heading text-white" style={{ fontSize: fs("1.1rem", "0.85rem"), marginTop: "0.25rem" }}>
            {voteData.law}
          </div>
        </div>
        <div className="font-heading text-[#aaa] text-center" style={{ fontSize: fs("0.9rem", "0.72rem") }}>
          Кто нарушил закон диктатора <span style={{ color: ACCENT }}>{voteData.dictator}</span>?
        </div>
        <div className="font-heading text-[#555]" style={{ fontSize: fs("0.8rem", "0.65rem") }}>
          Игроки голосуют на телефонах
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", justifyContent: "center" }}>
          {voters.map(p => {
            const pl = players.find(pl => pl.name === p);
            return (
              <div key={p} style={{
                background: "#ffffff0a", border: `1.5px solid ${pl?.color || "#555"}44`,
                borderRadius: "2rem", padding: "0.25rem 0.75rem",
                fontSize: fs("0.85rem", "0.7rem"), color: pl?.color || "#ccc",
              }}>⏳ {p}</div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Discussion ────────────────────────────────────────────────────────────
  if (data?.phase === "discussion" && law) {
    return (
      <div className="w-full animate-slide-up" style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: fs("1rem", "0.65rem"), maxWidth: "700px", margin: "0 auto",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
          <span style={{ fontSize: fs("2.5rem", "1.8rem") }}>👑</span>
          <span className="font-display" style={{ fontSize: fs("1.8rem", "1.3rem"), color: ACCENT }}>{law.dictator}</span>
          {timeLeft !== null && <Timer seconds={timeLeft} max={timerMax} />}
        </div>
        <div style={{
          background: "#FFD70022", border: `2.5px solid ${ACCENT}`,
          borderRadius: "1.2rem", padding: fs("1rem 2rem", "0.7rem 1.2rem"),
          textAlign: "center", width: "100%", boxShadow: `0 0 24px ${ACCENT}33`,
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize: fs("0.8rem", "0.62rem"), marginBottom: "0.3rem" }}>⚖️ ЗАКОН РАУНДА</div>
          <div className="font-heading text-white" style={{ fontSize: fs("1.4rem", "1rem"), lineHeight: 1.4 }}>{law.law}</div>
        </div>
        <div style={{
          background: "#111", border: "1.5px solid #333",
          borderRadius: "1rem", padding: fs("0.7rem 1.5rem", "0.5rem 1rem"),
          textAlign: "center", width: "100%",
        }}>
          <div className="font-heading text-[#aaa]" style={{ fontSize: fs("0.75rem", "0.6rem"), marginBottom: "0.25rem" }}>📋 ТЕМА РАЗГОВОРА</div>
          <div className="font-heading text-white" style={{ fontSize: fs("1rem", "0.8rem") }}>{data.task}</div>
        </div>
        <div style={{
          background: "#0a0a20", border: "1px solid #ffffff15",
          borderRadius: "0.75rem", padding: fs("0.5rem 1rem", "0.35rem 0.75rem"), textAlign: "center",
        }}>
          <span className="font-heading text-[#555]" style={{ fontSize: fs("0.72rem", "0.58rem") }}>
            Диктатор +100 · Угадавший нарушителя +50 · Без консенсуса все +20
          </span>
        </div>
        <div style={{
          background: "#0a0a20", border: "1px solid #3dffa033",
          borderRadius: "0.75rem", padding: fs("0.5rem 1rem", "0.35rem 0.75rem"), textAlign: "center",
        }}>
          <span className="font-heading text-[#3dffa0]" style={{ fontSize: fs("0.78rem", "0.62rem") }}>
            👑 {law.dictator} завершает обсуждение со своего телефона
          </span>
        </div>
      </div>
    );
  }

  // ── Waiting for law ───────────────────────────────────────────────────────
  return (
    <div className="w-full animate-slide-up" style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      gap: fs("1.2rem", "0.8rem"), maxWidth: "700px", margin: "0 auto", textAlign: "center",
    }}>
      <div style={{ fontSize: fs("3rem", "2rem") }}>👑</div>
      <div className="font-display" style={{ fontSize: fs("2rem", "1.4rem"), color: ACCENT }}>
        {data?.dictator} — Диктатор!
      </div>
      <div style={{
        background: "#111", border: "1.5px solid #333",
        borderRadius: "1rem", padding: fs("1rem 1.5rem", "0.7rem 1rem"), width: "100%",
      }}>
        <div className="font-heading text-[#aaa]" style={{ fontSize: fs("0.8rem", "0.65rem"), marginBottom: "0.3rem" }}>📋 ТЕМА РАЗГОВОРА</div>
        <div className="font-heading text-white" style={{ fontSize: fs("1.1rem", "0.85rem"), lineHeight: 1.4 }}>{data?.task}</div>
      </div>
      <div className="font-heading text-[#aaa]" style={{ fontSize: fs("0.9rem", "0.75rem") }}>
        ⏳ Диктатор придумывает закон на своём телефоне...
      </div>
      <div style={{
        background: "#0a0a20", border: "1px solid #ffffff15",
        borderRadius: "0.75rem", padding: fs("0.6rem 1.2rem", "0.4rem 0.8rem"),
      }}>
        <div className="font-heading text-[#444]" style={{ fontSize: fs("0.75rem", "0.6rem"), lineHeight: 1.6 }}>
          💡 Диктатор придумывает закон → все говорят по теме соблюдая закон →<br/>
          голосуют кто нарушил → консенсус = диктатор +100, угадавшие +50
        </div>
      </div>
      <div className="font-heading text-[#555]" style={{ fontSize: fs("0.8rem", "0.65rem") }}>
        Раунд {data?.round}/{data?.max_rounds}
      </div>
    </div>
  );
}